<template>
<div class="youtube-facade">
    <template v-if="isPlaying">
        <component
            :is="youtubeComponent"
            ref="youtube"
            :videoid="videoid"
            :loop="loop"
            :autoplay="1"
            :height="height"
            :width="width"
            :controls="controls"
        />
    </template>
    <template v-else>
        <CookieConsentGate v-if="!hasCookieConsent" />
        <button
            class="facade-button"
            :aria-label="`Play video`"
            v-on:click="play"
        >
            <img
                class="thumbnail"
                :src="`https://img.youtube.com/vi/${videoid}/maxresdefault.jpg`"
                :alt="alt || 'Video thumbnail'"
                loading="lazy"
                v-on:error="onThumbnailError"
            >
            <div class="play-overlay">
                <UiIcon
                    icon="play"
                    alt="play"
                    icon-color="white"
                    :classes="['play-icon']"
                />
            </div>
        </button>
    </template>
</div>
</template>

<script setup lang="ts">
import {
    ref,
    shallowRef,
    defineAsyncComponent,
    onMounted
} from 'vue'
import UiIcon from 'public/images/icons/ui/UiIcon.vue'
import CookieConsentGate from '$components/CookieConsentGate.vue'
import { CookieYes } from '$services/cookieyes'
import Category = CookieYes.Category

const props = withDefaults(defineProps<{
    videoid: string
    loop?: number
    height?: number
    width?: number
    controls?: number
    alt?: string
    eager?: boolean
}>(), {
    loop: 0,
    height: 315,
    width: 560,
    controls: 1,
    alt: '',
    eager: false
})

const hasCookieConsent = computed(() => {
    // embedded YouTube requires all 3 of Functional, Analytics, and Advertisement
    if (!CookieYes.hasConsent(Category.Functional)) {
        return false
    }
    if (!CookieYes.hasConsent(Category.Analytics)) {
        return false
    }
    return CookieYes.hasConsent(Category.Advertisement)
})

const isPlaying = ref(false)
const youtubeComponent = shallowRef<ReturnType<typeof defineAsyncComponent> | null>(null)
const youtube = ref(null)

function play() {
    youtubeComponent.value = defineAsyncComponent(
        () => import('youtube-vue3').then((m) => m.YoutubeVue3)
    )
    isPlaying.value = true
}

function onThumbnailError(e: Event) {
    const img = e.target as HTMLImageElement
    img.src = `https://img.youtube.com/vi/${props.videoid}/hqdefault.jpg`
}

onMounted(() => {
    if (props.eager) {
        play()
    }
})

defineExpose({ youtube })
</script>

<style lang="scss" scoped>
@use "$styles/kit.scss" as *;

.youtube-facade {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    background: #000;
    overflow: hidden;
}

.facade-button {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
}

.thumbnail {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.play-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s ease;
    z-index: 1;

    :deep(.play-icon) {
        width: 8%;
        min-width: 50px;
        height: auto;
        aspect-ratio: 1;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.40), 0 4px 4px rgba(0, 0, 0, 0.25);
        border-radius: 100px;
        opacity: 0.9;
    }

    &:hover {
        :deep(.play-icon) {
            opacity: 1;
        }
    }
}

:deep(iframe) {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
}
</style>
